import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import React, { useEffect, useMemo, useState } from 'react';
import { ClickAwayListener, MenuItem, MenuList, Paper, Popper } from '@mui/material';
import { ORDER_PREFERENCE_SET, getEmployeeId, getNotificationType, markNotificationAsRead } from './utils';
import { useDispatch } from 'react-redux';
import { taskDetailsActions } from '@AMIEWEB/Tasks/store/Tasks.redux';
import {
  getCandidateProfileCompanies,
  ICandidateProfileCompanyOption,
} from 'app/services/SharedServices/SharedServices';

const candidateCompaniesCache = new Map<string, ICandidateProfileCompanyOption[]>();

type TaskSource = 'placement' | 'order' | 'unit' | 'facility' | 'candidate' | 'unknown';

const getPositiveNumber = (value: unknown) => {
  const parsedValue = Number.parseInt(`${value ?? ''}`, 10);
  return Number.isFinite(parsedValue) && parsedValue > 0 ? parsedValue : 0;
};

const getTaskSource = (record: any): TaskSource => {
  const placementId = getPositiveNumber(record?.PlacementId);
  const orderId = getPositiveNumber(record?.OrderId);
  const unitId = getPositiveNumber(record?.UnitId);
  const facilityId = getPositiveNumber(record?.FacilityId ?? record?.FaciltyId);
  const candidateId = getPositiveNumber(record?.CandidateId);

  if (placementId > 0) return 'placement';
  if (orderId > 0) return 'order';
  if (unitId > 0) return 'unit';
  if (facilityId > 0) return 'facility';
  if (candidateId > 0) return 'candidate';

  return 'unknown';
};

export const RenderOptionsPopper = props => {
  const {
    args: {
      anchorEl2,
      handleClose,
      notifyData,
      classes,
      setAnchorEl2,
      record,
      closeFeed,
      t,
      // history,
      pageNumber,
      userInfo,
      isUnread,
      pushNotification = false,
    },
  } = props;
  const { id, useType, useSubType, channel } = notifyData?.contentProps;
  const dispatch = useDispatch();
  const [candidateCompanies, setCandidateCompanies] = useState<ICandidateProfileCompanyOption[]>([]);
  const [isCandidateCompaniesLoading, setIsCandidateCompaniesLoading] = useState(false);

  const candidateId = getPositiveNumber(record?.CandidateId);
  const placementId = getPositiveNumber(record?.PlacementId);
  const orderId = getPositiveNumber(record?.OrderId);
  const facilityId = getPositiveNumber(record?.FacilityId ?? record?.FaciltyId);
  const unitId = getPositiveNumber(record?.UnitId);
  const taskSource = useMemo(() => getTaskSource(record), [record]);
  const isCandidateOnlyTask = taskSource === 'candidate';
  const resolvedCandidateBrandId = `${record?.BrandId ?? candidateCompanies[0]?.brandId ?? '1'}`.trim() || '1';
  const candidatePath = `/candidate/${candidateId}/${resolvedCandidateBrandId}`;
  const candidatePathTab6 = `${candidatePath}?tab=6`;
  const placementPath = `/placement/${placementId}`;
  const orderPath = `/order/${orderId}`;
  const facilityPath = `/facility/${facilityId}`;
  const unitPath = `/facility/${facilityId}/unit/${unitId}`;
  const showCandidateCompanyChoices = isCandidateOnlyTask && candidateCompanies.length > 1;
  const showPlacementOption = placementId > 0 && taskSource === 'placement';
  const showCandidateOption =
    candidateId > 0 && ['candidate', 'facility', 'unit', 'order', 'placement'].includes(taskSource);
  const showOrderOption = orderId > 0 && (taskSource === 'order' || taskSource === 'placement');
  const showFacilityOption =
    facilityId > 0 && ['facility', 'unit', 'order', 'placement'].includes(taskSource);
  const showUnitOption = facilityId > 0 && unitId > 0 && ['unit', 'order', 'placement'].includes(taskSource);

  useEffect(() => {
    if (!anchorEl2 || !isCandidateOnlyTask || candidateId <= 0) {
      setCandidateCompanies([]);
      setIsCandidateCompaniesLoading(false);
      return;
    }

    const cacheKey = `${candidateId}`;
    const cachedCompanies = candidateCompaniesCache.get(cacheKey);
    if (cachedCompanies) {
      setCandidateCompanies(cachedCompanies);
      return;
    }

    let isMounted = true;
    setIsCandidateCompaniesLoading(true);

    getCandidateProfileCompanies(candidateId)
      .then(companies => {
        if (!isMounted) return;
        candidateCompaniesCache.set(cacheKey, companies);
        setCandidateCompanies(companies);
      })
      .catch(() => {
        if (!isMounted) return;
        setCandidateCompanies([]);
      })
      .finally(() => {
        if (!isMounted) return;
        setIsCandidateCompaniesLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [anchorEl2, candidateId, isCandidateOnlyTask]);

  const handleMarkAsRead = () => {
      const notificationCategory = getNotificationType({
        useType,
        useSubType,
        channelType: channel,
      });
      //Diasables the  service to update new data, only updates count
      const disableGetActivityFetch = true;
      markNotificationAsRead(
        [id],
        parseInt(getEmployeeId(userInfo)),
        dispatch,
        notificationCategory,
        pageNumber,
        isUnread,
        disableGetActivityFetch,
      );
      pushNotification && dispatch(taskDetailsActions.setDismissNotificationId(id));
  };

  const openPath = (path: string, onBeforeOpen?: () => void) => {
    handleMarkAsRead();
    onBeforeOpen?.();
    window.open(path, '_blank');
    closeFeed();
  };

  return (
    <Popper
      open={Boolean(anchorEl2)}
      anchorEl={anchorEl2}
      role={undefined}
      disablePortal={false}
      style={{ zIndex: 9999 }}
      className={classes.menu}
      placement="bottom-end"
      modifiers={[
        { name: 'flip', enabled: true },
        { name: 'preventOverflow', enabled: true, options: { boundary: 'viewport' } },
      ]}
    >
      <ClickAwayListener onClickAway={handleClose}>
        <Paper className={classes.paperMenu}>
          <MenuList
            onClick={e => {
              setAnchorEl2(null);
              e.stopPropagation();
            }}
          >
            {candidateId > 0 && record?.PreferenceSetName && (
              <MenuItem
                className={classes.menuItem}
                onClick={() => {
                  openPath(candidatePathTab6, () => localStorage.setItem(ORDER_PREFERENCE_SET, JSON.stringify(record)));
                }}
              >
                {t('notification.taskNotification.PreferenceSet')}
              </MenuItem>
            )}
            {showPlacementOption && (
              <MenuItem
                className={classes.menuItem}
                onClick={() => {
                  openPath(placementPath);
                }}
              >
                { t('notification.taskNotification.placementOption')}
              </MenuItem>
            )}

            {showCandidateOption && isCandidateCompaniesLoading && (
              <>
                <MenuItem className={classes.menuItem} onClick={e => e.stopPropagation()}>
                  {t('notification.taskNotification.CandidateOption')}
                </MenuItem>
                <MenuItem className={classes.menuItem} onClick={e => e.stopPropagation()} sx={{ pl: 4 }}>
                  {t('notification.taskNotification.candidateProfilesLoading')}
                </MenuItem>
              </>
            )}

            {showCandidateOption && !isCandidateCompaniesLoading && showCandidateCompanyChoices && (
              <>
                <MenuItem className={classes.menuItem} onClick={e => e.stopPropagation()}>
                  {t('notification.taskNotification.CandidateOption')}
                  <KeyboardArrowRightIcon fontSize="small" sx={{ ml: 'auto' }} />
                </MenuItem>
                {candidateCompanies.map(company => (
                  <MenuItem
                    key={company.brandId}
                    className={classes.menuItem}
                    sx={{ pl: 4 }}
                    onClick={() => {
                      openPath(`/candidate/${candidateId}/${company.brandId}`);
                    }}
                  >
                    {company.brand}
                  </MenuItem>
                ))}
              </>
            )}

            {showCandidateOption && !isCandidateCompaniesLoading && !showCandidateCompanyChoices && (
              <MenuItem
                className={classes.menuItem}
                onClick={() => {
                  openPath(candidatePath);
                }}
              >
                { t('notification.taskNotification.CandidateOption')}
              </MenuItem>
            )}

            {showOrderOption && (
              <MenuItem
                className={classes.menuItem}
                onClick={() => {
                  openPath(orderPath);
                }}
              >
                { t('notification.taskNotification.OrderOption')}
              </MenuItem>
            )}

            {showFacilityOption && (
              <MenuItem
                className={classes.menuItem}
                onClick={() => {
                  openPath(facilityPath);
                }}
              >
                { t('notification.taskNotification.facilityOption')}
              </MenuItem>
            )}
            {showUnitOption && (
                <MenuItem
                  className={classes.menuItem}
                  onClick={() => {
                    openPath(unitPath);
                  }}
                >
                  {t('notification.taskNotification.UnitOption')}
                </MenuItem>
              )}
          </MenuList>
        </Paper>
      </ClickAwayListener>
    </Popper>
  );
};
